import type { PrintDataFields } from "../hooks/use-print-data";
import type { PrintFieldDef, PrintSectionDef } from "./types";

export const ARBRE_SECTIONS: PrintSectionDef[] = [
  { id: "identification", title: "", defaultColumns: 3 },
  {
    id: "caracteristiques",
    title: "Caractéristiques",
    pdfTitle: "CARACTÉRISTIQUES",
    defaultColumns: 3,
  },
  {
    id: "environnement",
    title: "Environnement",
    pdfTitle: "ENVIRONNEMENT",
    defaultColumns: 3,
  },
  {
    id: "diagnostic_visuel",
    title: "Diagnostic visuel",
    pdfTitle: "DIAGNOSTIC VISUEL",
    defaultColumns: 1,
  },
  {
    id: "diagnostic_sanitaire",
    title: "Diagnostic sanitaire",
    pdfTitle: "Diagnostic sanitaire",
    defaultColumns: 1,
  },
  { id: "commentaires", title: "", defaultColumns: 1 },
];

export const text = <TData = PrintDataFields>(
  key: string,
  section: string,
  label: string,
  defaultCol: number,
  resolve: PrintFieldDef<TData>["resolve"],
  extra: Partial<PrintFieldDef<TData>> = {},
): PrintFieldDef<TData> => ({
  key,
  section,
  label,
  kind: "text",
  defaultCol,
  defaultVisible: true,
  resolve,
  ...extra,
});

export const block = <TData = PrintDataFields>(
  key: string,
  section: string,
  label: string,
  extra: Partial<PrintFieldDef<TData>> = {},
): PrintFieldDef<TData> => ({
  key,
  section,
  label,
  kind: "block",
  defaultCol: 1,
  defaultVisible: true,
  resolve: () => null,
  ...extra,
});

/** Champs de la fiche arbre, dans l'ordre et les colonnes de la fiche actuelle (PrintPage). */
export const ARBRE_FIELDS: PrintFieldDef[] = [
  // Identification
  text("date", "identification", "Date", 1, (d) => d.formattedDate, {
    locked: true,
  }),
  text(
    "genre_latin",
    "identification",
    "Genre (latin)",
    1,
    (d) => d.genreLatin,
  ),
  text("nom_fr", "identification", "Nom (FR)", 1, (d) => d.especeLabel),
  text("adresse", "identification", "Adresse", 1, (d) => d.addressLine),
  text("code_site", "identification", "Code", 2, (d) => d.code_site, {
    locked: true,
    pdfLabel: "Code site", // le PDF actuel écrit « Code site », la fiche web « Code »
  }),
  text("espece_latin", "identification", "Espèce", 2, (d) => d.especeLatin),
  text(
    "identification",
    "identification",
    "Identification",
    3,
    (d) => d.num_sujet,
    { locked: true },
  ),
  text("cultivar", "identification", "Cultivar", 3, (d) => d.cultivar, {
    pdfCol: 2, // le PDF actuel place le cultivar sous l'espèce, la fiche web en 3e colonne
  }),
  text("gps", "identification", "Coordonnées GPS", 3, (d) => d.gps),

  // Caractéristiques
  text("hauteur", "caracteristiques", "Hauteur en m", 1, (d) => d.hauteur),
  text("diametre", "caracteristiques", "Diamètre en cm", 1, (d) => d.diametre),
  text("age_estime", "caracteristiques", "Âge estimé", 1, (d) => d.age_estime),
  text(
    "stade_dev",
    "caracteristiques",
    "Stade de dév.",
    1,
    (d) => d.caracteristiques?.stade_dev,
  ),
  text(
    "remarquable",
    "caracteristiques",
    "Remarquable",
    1,
    (d) => d.caracteristiques?.critere,
  ),
  text(
    "port_arbre",
    "caracteristiques",
    "Port",
    2,
    (d) => d.caracteristiques?.port_arbre,
  ),
  text(
    "caract_tronc",
    "caracteristiques",
    "Tronc",
    2,
    (d) => d.caracteristiques?.caract_tronc,
  ),
  text(
    "caract_pied",
    "caracteristiques",
    "Pied de l'arbre",
    2,
    (d) => d.caracteristiques?.caract_pied,
  ),
  text(
    "implantation",
    "caracteristiques",
    "Implantation",
    3,
    (d) => d.caracteristiques?.implantation,
  ),
  text(
    "domaine",
    "caracteristiques",
    "Domaine",
    3,
    (d) => d.caracteristiques?.domaine,
  ),
  text(
    "situation",
    "caracteristiques",
    "Situation",
    3,
    (d) => d.caracteristiques?.situation,
  ),
  text(
    "acces",
    "caracteristiques",
    "Accès",
    3,
    (d) => d.caracteristiques?.accessibilite,
  ),

  // Environnement
  text(
    "proximite",
    "environnement",
    "Cohabitation",
    1,
    (d) => d.environnement?.proximite,
  ),
  text("dict", "environnement", "Demande de DICT", 1, (d) => d.dictRequired, {
    pdfResolve: (d) => d.environnement?.dictLabel, // le PDF actuel lit le libellé de la DICT, la fiche web « Oui/Non »
  }),
  text(
    "nuisance",
    "environnement",
    "Nuisance",
    2,
    (d) => d.environnement?.nuisance,
  ),
  text(
    "taux_freq",
    "environnement",
    "Fréquentation",
    3,
    (d) => d.environnement?.taux_freq,
  ),
  text(
    "type_passage",
    "environnement",
    "Type de Passage",
    3,
    (d) => d.environnement?.type_passage,
  ),

  // Blocs riches : seule leur place et leur visibilité sont personnalisables
  block("bloc_diagnostic_visuel", "diagnostic_visuel", "Diagnostic visuel"),
  block(
    "bloc_diagnostic_sanitaire",
    "diagnostic_sanitaire",
    "Diagnostic sanitaire",
  ),
  block("bloc_commentaires", "commentaires", "Commentaires"),
];
